import { convertUnitToSubUnit, unitDivisor } from '../../util/currency';
import {
  addTime,
  daysBetween,
  findNextBoundary,
  getEndOfDay,
  getStartOfDay,
  isDate,
  isSameDate,
  parseDateFromISO8601,
  subtractTime,
} from '../../util/dates';
import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { constructQueryParamName, isOriginInUse } from '../../util/search';
import { parse } from '../../util/urlHelpers';

import { sendTrackingEvent, TRACKING_EVENTS } from '../../analytics/trackingEvent';
import { TimeslotsDTO } from '../../domain/dtos';
import { filterVisibleListings } from '../../domain/listingVisibility';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { denormalisedResponseEntities } from '../../util/data';
import {
  DIGITAL_PRODUCT_LISTING_TYPES,
  isGroupSession,
} from '../../util/soulwise-marketplace/listingType';
import { cacheTimeslots, getCachedTimeslots } from '../../util/timeslotsCache';
import {
  GROUP_SESSION_LISTING_TYPES_AS_SEARCH_QUERY,
  INDIVIDUAL_SESSION_LISTING_TYPES_AS_SEARCH_QUERY,
} from '../LandingPage/domain';

/**
 * Parse a date string in the format "YYYY-MM-DD,YYYY-MM-DD" into an array of Date objects.
 * @param {string} dateString - The date string to parse
 * @returns {Array} Array of Date objects, or empty array if invalid
 */
const parseDateString = dateString => {
  if (!dateString) return [];
  const values = dateString.split(',');
  if (values.length !== 2) return [];

  const dates = values.map(value => new Date(value));
  return dates.every(isDate) ? dates : [];
};

/**
 * Track search event only if there's evidence of user-initiated search.
 * @param {Object} searchParams - Original search parameters
 * @param {Object} baseParams - Processed base parameters
 * @param {Object} restOfParams - Additional search parameters
 * @param {string} dates - Date range string
 * @param {Object} results - Search results
 */
const trackSearchEvent = (searchParams, baseParams, restOfParams, dates, results) => {
  // Calculate days between dates if date range exists
  const datesRange = (() => {
    const [startDate, endDate] = parseDateString(dates);

    if (!startDate || !endDate) return null;

    // Calculate inclusive date range (counting both start and end dates)
    // For dates like March 10-11, this should return 2

    // First check if the dates are the same
    if (isSameDate(startDate, endDate)) {
      return 1; // Same day
    }

    // Otherwise calculate days between and add 1 to make it inclusive
    return daysBetween(startDate, endDate) + 1;
  })();

  const trackingParams = {
    search_term: baseParams?.keywords,
    search_dates_range: datesRange,
    listing_category_level1: baseParams?.pub_categoryLevel1,
    listing_venue_name: baseParams?.pub_venueName,
    listing_mode: baseParams?.pub_mode,
    listing_language: baseParams?.pub_language,
    listing_type: restOfParams?.pub_listingType,
    results_count: results?.data?.data?.length,
  };

  // Only send tracking event if any search parameter is defined (proof of user search)
  const hasSearchParams = Object.entries(trackingParams).some(([key, value]) => {
    // Exclude results_count as it will always be defined (at least as 0)
    return key !== 'results_count' && value !== undefined && value !== null && value !== '';
  });

  if (hasSearchParams) {
    sendTrackingEvent({
      name: TRACKING_EVENTS.SEARCH,
      parameters: trackingParams,
    });

    if (trackingParams?.results_count === 0) {
      sendTrackingEvent({
        name: TRACKING_EVENTS.SEARCH_WITH_NO_RESULTS,
        parameters: trackingParams,
      });
    }
  }
};

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 4 columns 12 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 100;

// ================ Action types ================ //

export const SEARCH_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_LISTINGS_REQUEST';
export const SEARCH_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_LISTINGS_SUCCESS';
export const SEARCH_LISTINGS_ERROR = 'app/SearchPage/SEARCH_LISTINGS_ERROR';

export const TIME_SLOTS_FOR_LISTING_REQUEST = 'app/SearchPage/TIME_SLOTS_FOR_LISTING_REQUEST';
export const TIME_SLOTS_FOR_LISTING_SUCCESS = 'app/SearchPage/TIME_SLOTS_FOR_LISTING_SUCCESS';
export const TIME_SLOTS_FOR_LISTING_ERROR = 'app/SearchPage/TIME_SLOTS_FOR_LISTING_ERROR';
export const TIME_SLOTS_FOR_LISTING_REQUEST_END =
  'app/SearchPage/TIME_SLOTS_FOR_LISTING_REQUEST_END';

export const SEARCH_MAP_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_MAP_LISTINGS_REQUEST';
export const SEARCH_MAP_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_MAP_LISTINGS_SUCCESS';
export const SEARCH_MAP_LISTINGS_ERROR = 'app/SearchPage/SEARCH_MAP_LISTINGS_ERROR';

export const SEARCH_MAP_SET_ACTIVE_LISTING = 'app/SearchPage/SEARCH_MAP_SET_ACTIVE_LISTING';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  searchParams: null,
  searchInProgress: false,
  timeslotsPerListingInProgress: false,
  timeslotsPerListingError: null,
  searchListingsError: null,
  currentPageResultIds: [],
  timeslotsPerListing: {},
};

const resultIds = data => {
  const listings = data.data;
  return filterVisibleListings(listings).map(l => l.id);
};

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SEARCH_LISTINGS_REQUEST:
      return {
        ...state,
        searchParams: payload.searchParams,
        searchInProgress: true,
        searchMapListingIds: [],
        searchListingsError: null,
      };
    case SEARCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        searchInProgress: false,
      };
    case SEARCH_LISTINGS_ERROR:
      console.error(payload);
      return { ...state, searchInProgress: false, searchListingsError: payload };

    case SEARCH_MAP_SET_ACTIVE_LISTING:
      return {
        ...state,
        activeListingId: payload,
      };
    case TIME_SLOTS_FOR_LISTING_REQUEST:
      return {
        ...state,
        timeslotsPerListingInProgress: true,
      };
    case TIME_SLOTS_FOR_LISTING_SUCCESS:
      const { listingId, timeslots } = payload;
      const newTimeSlots = timeslots?.filter(ts => {
        const { start, end } = ts.attributes;
        return !state.timeslotsPerListing?.[listingId]?.timeslots?.find(
          ts => ts.attributes.start === start && ts.attributes.end === end
        );
      });
      return {
        ...state,
        timeslotsPerListingInProgress: false,
        timeslotsPerListing: {
          ...state.timeslotsPerListing,
          [listingId]: [
            ...(state.timeslotsPerListing?.[listingId]?.timeslots || []),
            ...newTimeSlots,
          ],
        },
      };
    case TIME_SLOTS_FOR_LISTING_ERROR:
      console.error(payload);
      return { ...state, timeslotsPerListingInProgress: false, timeslotsPerListingError: payload };
    case TIME_SLOTS_FOR_LISTING_REQUEST_END:
      return { ...state, timeslotsPerListingInProgress: false };
    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const searchListingsRequest = searchParams => ({
  type: SEARCH_LISTINGS_REQUEST,
  payload: { searchParams },
});

export const searchListingsSuccess = response => ({
  type: SEARCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const searchListingsError = e => ({
  type: SEARCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const timeSlotForListingRequest = () => ({
  type: TIME_SLOTS_FOR_LISTING_REQUEST,
});

const timeSlotForListingSuccess = response => ({
  type: TIME_SLOTS_FOR_LISTING_SUCCESS,
  payload: response,
});

const timeSlotForListingEnded = response => ({
  type: TIME_SLOTS_FOR_LISTING_REQUEST_END,
  payload: response,
});

// SearchPage need to use a single time zone but listings can have different time zones
// We need to expand/prolong the time window (start & end) to cover other time zones too.
//
// NOTE: you might want to consider changing UI so that
//   1) location is always asked first before date range
//   2) use some 3rd party service to convert location to time zone (IANA tz name)
//   3) Make exact dates filtering against that specific time zone
//   This setup would be better for dates filter,
//   but it enforces a UX where location is always asked first and therefore configurability
export const getSearchProlongedStart = (date, searchTZ) =>
  subtractTime(date, 14, 'hours', searchTZ);
export const getSearchProlongedEnd = (date, searchTZ) => addTime(date, 12, 'hours', searchTZ);

export const timeSlotForListingError = e => ({
  type: TIME_SLOTS_FOR_LISTING_ERROR,
  error: true,
  payload: e,
});

const buildTimeslotsEntry = (timeslots, listingId) => {
  return {
    timeslots: TimeslotsDTO(timeslots).map(ts => ({ ...ts, listingId })),
    listingId,
  };
};

export const fetchTimeslots = (listingIds = null, dates = {}) => (dispatch, getState, sdk) => {
  const {
    SearchPage: { currentPageResultIds, searchParams },
    marketplaceData: { entities },
  } = getState();

  // Only group sessions are displayed with timeslots
  const groupSessionListings = (listingIds || currentPageResultIds)
    .map(({ uuid }) => entities.listing[uuid])
    .filter(i => isGroupSession(i.attributes.publicData.listingType));

  dispatch(timeSlotForListingRequest());

  const timeslotPromises = groupSessionListings.map(listing => {
    const {
      attributes: { availabilityPlan },
    } = listing;

    const hasTimeZone = availabilityPlan && availabilityPlan.timezone;
    const tz = availabilityPlan.timezone;

    if (!listing.id || !hasTimeZone) {
      return Promise.resolve(null);
    }

    const nearestHourDate = findNextBoundary(new Date(), 'hour', tz);
    const nextMonthDate = addTime(nearestHourDate, 30, 'days', tz);

    // If no search dates passed from actual search take first 30 days
    const start = dates?.start || getSearchProlongedStart(nearestHourDate, tz);
    const end = dates?.end || getSearchProlongedEnd(nextMonthDate, tz);

    // IMPORTANT: params keys are used as cache keys. When changing them, make sure to update the cache key in the cacheTimeslots functions
    const params = {
      listingId: listing.id,
      start,
      end,
      perPage: 500,
      page: 1,
    };

    // Tag: #skip-fetching-timeslots-if-fetched-in-last-5-minutes
    const cachedResponse = getCachedTimeslots(params);
    if (cachedResponse) {
      return Promise.resolve(
        buildTimeslotsEntry(denormalisedResponseEntities(cachedResponse), listing.id.uuid)
      );
    }

    return sdk.timeslots
      .query(params)
      .then(response => {
        cacheTimeslots(params, response);
        return buildTimeslotsEntry(denormalisedResponseEntities(response), listing.id.uuid);
      })
      .catch(e => {
        dispatch(timeSlotForListingError(storableError(e)));
        return null;
      });
  });

  return Promise.all(timeslotPromises).then(results => {
    const validResults = results.filter(data => data !== null);
    validResults.forEach(data => {
      dispatch(timeSlotForListingSuccess(data));
    });

    dispatch(timeSlotForListingEnded());
  });
};

const isValidFutureDate = (dateString, timezone) => {
  const currentDate = new Date();
  const parsedDate = new Date(dateString);
  if (!isDate(parsedDate)) return false;
  const oneYearFromNow = addTime(currentDate, 1, 'year', timezone);
  return parsedDate <= oneYearFromNow;
};

const parseDates = (input, timezone) => {
  const values = input.split(',');
  if (values.length !== 2 || !values.every(date => isValidFutureDate(date, timezone))) return [];
  return values.map(date => parseDateFromISO8601(date, timezone));
};

const addDays = (date, daysToAdd, timezone) =>
  getEndOfDay(addTime(date, daysToAdd, 'days', timezone), timezone);

export const SEARCH_TIMEZONE = 'Etc/UTC';
export const DEFAULT_SEARCH_DAYS_SPAN = 90;
export const datesSearchParams = (datesParam = '', categoryLevel1) => {
  const currentDate = new Date();
  const todayStart = getStartOfDay(currentDate, SEARCH_TIMEZONE);
  const todayEnd = getEndOfDay(currentDate, SEARCH_TIMEZONE);

  const [inputStartDate, inputEndDate] = parseDates(datesParam, SEARCH_TIMEZONE);

  const startDate = inputStartDate ? getStartOfDay(inputStartDate, SEARCH_TIMEZONE) : todayStart;
  const endDate = inputEndDate ? getEndOfDay(inputEndDate, SEARCH_TIMEZONE) : todayEnd;

  // If the startDate is in the past, use today as the start date
  const validStartDate = startDate.getTime() >= todayStart.getTime() ? startDate : todayStart;
  // If the end date is in the past, use today as the end date
  const validEndDate =
    endDate.getTime() >= validStartDate.getTime()
      ? endDate
      : addDays(validStartDate, 1, SEARCH_TIMEZONE);

  const daysBetweenStartAndEnd = daysBetween(validStartDate, validEndDate);
  const IMMEDIATE_DAYS_SPAN = 2;
  // ranga cannot have more than 90 days
  const SEARCH_DAYS_SPAN = DEFAULT_SEARCH_DAYS_SPAN - daysBetweenStartAndEnd - 2;

  const immediateEndDate = addDays(validStartDate, IMMEDIATE_DAYS_SPAN, SEARCH_TIMEZONE);
  const immediateRangeDates = {
    start: getSearchProlongedStart(validStartDate, SEARCH_TIMEZONE),
    end: getSearchProlongedEnd(immediateEndDate, SEARCH_TIMEZONE),
  };

  const futureRangeEndDate = addDays(validEndDate, SEARCH_DAYS_SPAN, SEARCH_TIMEZONE);
  const futureRangeDates = {
    start: getSearchProlongedStart(immediateEndDate, SEARCH_TIMEZONE),
    end: getSearchProlongedEnd(futureRangeEndDate, SEARCH_TIMEZONE),
  };

  const commonDateParams = {
    availability: 'time-partial',
    minDuration: 15,
  };

  return {
    immediateRangeDateParams: {
      ...immediateRangeDates,
      ...commonDateParams,
    },
    futureRangeDateParams: {
      ...futureRangeDates,
      ...commonDateParams,
    },
  };
};

export const searchListings = (searchParams, config) => (dispatch, getState, sdk) => {
  dispatch(searchListingsRequest(searchParams));

  // SearchPage can enforce listing query to only those listings with valid listingType
  // NOTE: this only works if you have set 'enum' type search schema to listing's public data fields
  //       - listingType
  //       Same setup could be expanded to 2 other extended data fields:
  //       - transactionProcessAlias
  //       - unitType
  //       ...and then turned enforceValidListingType config to true in configListing.js
  // Read More:
  // https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
  const searchValidListingTypes = listingTypes => {
    return config.listing.enforceValidListingType
      ? {
          pub_listingType: listingTypes.map(l => l.listingType),
          // pub_transactionProcessAlias: listingTypes.map(l => l.transactionType.alias),
          // pub_unitType: listingTypes.map(l => l.transactionType.unitType),
        }
      : {};
  };

  const omitInvalidCategoryParams = params => {
    const categoryConfig = config.search.defaultFilters?.find(f => f.schemaType === 'category');
    const categories = config.categoryConfiguration.categories;
    const { key: prefix, scope } = categoryConfig || {};
    const categoryParamPrefix = constructQueryParamName(prefix, scope);

    const validURLParamForCategoryData = (prefix, categories, level, params) => {
      const levelKey = `${categoryParamPrefix}${level}`;
      const levelValue = params?.[levelKey];
      const foundCategory = categories.find(cat => cat.id === levelValue);
      const subcategories = foundCategory?.subcategories || [];
      return foundCategory && subcategories.length > 0
        ? {
            [levelKey]: levelValue,
            ...validURLParamForCategoryData(prefix, subcategories, level + 1, params),
          }
        : foundCategory
        ? { [levelKey]: levelValue }
        : {};
    };

    const categoryKeys = validURLParamForCategoryData(prefix, categories, 1, params);
    const nonCategoryKeys = Object.entries(params).reduce(
      (picked, [k, v]) => (k.startsWith(categoryParamPrefix) ? picked : { ...picked, [k]: v }),
      {}
    );

    return { ...nonCategoryKeys, ...categoryKeys };
  };

  const priceSearchParams = priceParam => {
    const inSubunits = value => convertUnitToSubUnit(value, unitDivisor(config.currency));
    const values = priceParam ? priceParam.split(',') : [];
    return priceParam && values.length === 2
      ? {
          price: [inSubunits(values[0]), inSubunits(values[1]) + 1].join(','),
        }
      : {};
  };

  const stockFilters = datesMaybe => {
    const hasDatesFilterInUse = Object.keys(datesMaybe).length > 0;

    // If dates filter is not in use,
    //   1) Add minStock filter with default value (1)
    //   2) Add relaxed stockMode: "match-undefined"
    // The latter is used to filter out all the listings that explicitly are out of stock,
    // but keeps bookable and inquiry listings.
    return hasDatesFilterInUse ? {} : { minStock: 1, stockMode: 'match-undefined' };
  };

  const isDigitalListingTypeFilter = pubListingTypeParam => {
    if (!pubListingTypeParam || typeof pubListingTypeParam !== 'string') {
      return false;
    }
    const types = pubListingTypeParam
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
    return types.length > 0 && types.every(t => DIGITAL_PRODUCT_LISTING_TYPES.includes(t));
  };

  const {
    perPage = RESULT_PAGE_SIZE,
    price,
    dates,
    sort,
    mapSearch,
    ...restOfParams
  } = searchParams;
  const priceMaybe = priceSearchParams(price);
  const { immediateRangeDateParams, futureRangeDateParams } = datesSearchParams(
    dates,
    searchParams.pub_categoryLevel1
  );
  const stockMaybe = isDigitalListingTypeFilter(restOfParams?.pub_listingType)
    ? {}
    : stockFilters(futureRangeDateParams);
  const sortMaybe = sort === config.search.sortConfig.relevanceKey ? {} : { sort };

  const baseParams = {
    ...omitInvalidCategoryParams(restOfParams),
    ...priceMaybe,
    ...stockMaybe,
    ...sortMaybe,
    ...searchValidListingTypes(config.listing.listingTypes),
    perPage,
  };

  // Digital listings are products (no bookable timeslots). Availability/date filters
  // (time-partial + start/end) exclude them from results — omit those params when the URL
  // filter is digital-only.
  const isDigitalOnlyListingTypeFilter = isDigitalListingTypeFilter(restOfParams?.pub_listingType);

  const immediateParams = isDigitalOnlyListingTypeFilter
    ? { ...baseParams }
    : {
        ...baseParams,
        ...immediateRangeDateParams,
      };

  const futureParams = isDigitalOnlyListingTypeFilter
    ? { ...baseParams }
    : {
        ...baseParams,
        ...futureRangeDateParams,
      };

  // When no pub_listingType listing type selected:
  // We are showing both individual and group sessions on the main page
  // To show decent number for both sessions we need to maka dedicated calls
  // to avoid situation when we fetch 97 group sessions and 3 individual
  //
  // When pub_listingType selected
  // Make regular call
  //
  // IMPORTANT: This is a workaround which will most probably will not work with pagination
  const paramsToQuery = isDigitalOnlyListingTypeFilter
    ? [{ ...baseParams }]
    : restOfParams?.pub_listingType
    ? [{ ...immediateParams }, { ...futureParams }]
    : [
        { ...immediateParams, pub_listingType: GROUP_SESSION_LISTING_TYPES_AS_SEARCH_QUERY },
        { ...immediateParams, pub_listingType: INDIVIDUAL_SESSION_LISTING_TYPES_AS_SEARCH_QUERY },
        { ...futureParams, pub_listingType: GROUP_SESSION_LISTING_TYPES_AS_SEARCH_QUERY },
        { ...futureParams, pub_listingType: INDIVIDUAL_SESSION_LISTING_TYPES_AS_SEARCH_QUERY },
      ];

  const responseShape = {
    data: {
      data: [],
      included: [],
      meta: {
        page: 0,
        paginationLimit: 0,
        perPage: 0,
        totalItems: 0,
        totalPages: 0,
      },
    },
  };

  function mergeResponses(responses, responseShape) {
    const seenIds = new Set();

    return responses.reduce(
      (acc, response) => {
        // Only add listings that haven't been seen before
        const uniqueListings =
          response?.data?.data?.filter(item => {
            if (seenIds.has(item.id.uuid)) return false;
            seenIds.add(item.id.uuid);
            return true;
          }) || [];

        acc.data.data.push(...uniqueListings);
        acc.data.included.push(...(response?.data?.included || []));

        // Simple addition for meta counts
        acc.data.meta.totalItems += response?.data?.meta?.totalItems || 0;
        acc.data.meta.totalPages = Math.max(
          acc.data.meta.totalPages,
          response?.data?.meta?.totalPages || 0
        );

        return acc;
      },
      { ...responseShape }
    );
  }

  return Promise.all(paramsToQuery.map(params => sdk.listings.query(params)))
    .then(responses => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };
      const mergedResponses = mergeResponses(responses, responseShape);
      dispatch(addMarketplaceEntities(mergedResponses, sanitizeConfig));
      dispatch(searchListingsSuccess(mergedResponses));
      dispatch(
        fetchTimeslots(null, {
          start: immediateRangeDateParams.start,
          end: futureRangeDateParams.end,
        })
      );

      // Prepare tracking parameters
      trackSearchEvent(searchParams, baseParams, restOfParams, dates, mergedResponses);

      return mergedResponses;
    })
    .catch(e => {
      dispatch(searchListingsError(storableError(e)));
      throw e;
    });
};

export const setActiveListing = listingId => ({
  type: SEARCH_MAP_SET_ACTIVE_LISTING,
  payload: listingId,
});

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  const { page = 1, address, origin, ...rest } = queryParams;
  const originMaybe = isOriginInUse(config) && origin ? { origin } : {};

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const searchListingsCall = searchListings(
    {
      ...rest,
      ...originMaybe,
      page,
      perPage: RESULT_PAGE_SIZE,
      include: ['author', 'author.profileImage', 'images'],
      'fields.listing': [
        'title',
        'geolocation',
        'price',
        'deleted',
        'state',
        'availabilityPlan',
        'publicData.listingType',
        'publicData.categoryLevel1',
        'publicData.categoryLevel2',
        'publicData.mode',
        'publicData.dateTime',
        'publicData.eventLocationDetails',
        'publicData.transactionProcessAlias',
        'publicData.unitType',
        'publicData.averageReviewRating',
        'publicData.location',
        'publicData.duration',
        'publicData.unlisted',
        'publicData.visibility',
        'publicData.digitalProductContentType',
        'publicData.digitalProductFormat',
        'publicData.digitalMedia',
        // These help rendering of 'purchase' listings,
        // when transitioning from search page to listing page
        'publicData.pickupEnabled',
        'publicData.shippingEnabled',
      ],
      'fields.user': [
        'profile.displayName',
        'profile.abbreviatedName',
        'profile.publicData.reviewStats',
        'profile.publicData.handle',
      ],
      'fields.image': [
        'variants.scaled-small',
        'variants.scaled-medium',
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,
        // Social media
        'variants.facebook',
        'variants.twitter',
        // Avatars
        'variants.square-small',
        'variants.square-small2x',
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      'limit.images': 1,
    },
    config
  );
  return dispatch(searchListingsCall);
};
